import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormControlLabel, Switch, Alert, Tooltip, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { sa, SectionHeaderRow, PillButton, Card } from '../superAdminUi';
import {
  searchUsers,
  sendSetPasswordLink,
  resetUserMfa,
  deleteUser,
  reactivateUser,
  getOrgMfaEnrollment,
  setOrgMfaEnrollment,
} from '../../services/api/identityUserService';
import {
  buildSearchRequest,
  mapUser,
  SORT_FIELDS,
  SCOPE_TYPE,
  interpretOrgMfaEnrolled,
  interpretOrgMfaLevel,
} from './userFields';
import { STATUS_FILTER_OPTIONS, DEFAULT_STATUS_FILTER } from './userLifecycle';
import UsersTable from './UsersTable';
import StatusFilterMenu from './StatusFilterMenu';
import AddUserModal from './modals/AddUserModal';
import ChangeEmailModal from './modals/ChangeEmailModal';
import ConfirmActionModal from './modals/ConfirmActionModal';

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  font-family: ${sa.font.family};
`;

// The header search box, matching the Figma "Search user…" field: bordered, leading icon.
const SearchBox = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  svg {
    position: absolute;
    left: 12px;
    font-size: 18px;
    color: ${sa.color.textGray};
    pointer-events: none;
  }
  input {
    width: 260px;
    max-width: 100%;
    border: 1px solid ${sa.color.stroke};
    border-radius: ${sa.radius.sm};
    background: ${sa.color.white};
    font-family: ${sa.font.family};
    font-size: 13px;
    color: ${sa.color.text};
    padding: 9px 12px 9px 38px;

    &::placeholder {
      color: ${sa.color.textGray};
    }
    &:focus {
      outline: none;
      border-color: ${sa.color.teal};
    }
  }
`;

const MfaControl = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  .MuiFormControlLabel-label {
    font-family: ${sa.font.family};
    font-size: 13px;
    color: ${sa.color.text};
  }
`;

const StatusFilter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    font-family: ${sa.font.family};
    font-size: 13px;
    color: ${sa.color.textGray};
  }
`;

// The confirm-only account actions. Change Email and Add User have their own form modals.
const CONFIRM_ACTIONS = {
  resetPassword: {
    title: 'Reset Password',
    confirmLabel: 'Send Link',
    successMessage: 'A set-password link has been sent to the user.',
    describe: (user) =>
      `Send a set-password link to ${user.email}. The user follows the link to choose a new password; their current password stays valid until they do.`,
    run: (user) => sendSetPasswordLink({ userId: user.id, email: user.email }),
  },
  resetMfa: {
    title: 'Reset MFA',
    confirmLabel: 'Reset MFA',
    successMessage: 'The user’s MFA enrolment has been reset.',
    describe: () =>
      'This clears the current authenticator enrolment and forces the user to set up MFA again on their next sign-in. It does NOT disable MFA — the account still requires it.',
    run: (user) => resetUserMfa(user.id),
  },
  delete: {
    title: 'Delete User',
    confirmLabel: 'Delete User',
    destructive: true,
    successMessage: 'The user has been deleted.',
    describe: (user) =>
      `Delete ${user.name || user.email}? They will lose access immediately. This can be undone by reactivating the account.`,
    run: (user) => deleteUser(user.id),
  },
  reactivate: {
    title: 'Reactivate User',
    confirmLabel: 'Reactivate',
    successMessage: 'The user has been reactivated.',
    describe: (user) => `Reactivate ${user.name || user.email} and restore their access?`,
    run: (user) => reactivateUser(user.id),
  },
};

const DEFAULT_PAGE_SIZE = 10;

function UserManagementSection({ scope, schoolId, groupId, parentGroupId, scopeName, schoolNamesById }) {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState(SORT_FIELDS.name);
  const [sortDescending, setSortDescending] = useState(false);
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  // The group's schools, offered as the Add User "Sites" options.
  const siteOptions = useMemo(
    () =>
      Object.entries(schoolNamesById || {}).map(([id, name]) => ({ id: Number(id), name })),
    [schoolNamesById]
  );

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [mfaReadable, setMfaReadable] = useState(true);
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaError, setMfaError] = useState('');
  // Inherited: on because the parent group requires MFA (school has no own policy).
  // Overridden: the school has explicitly opted OUT of the group's requirement.
  // groupRequired: the parent group's own org MFA state (drives what "off" writes).
  const [mfaInherited, setMfaInherited] = useState(false);
  const [mfaOverridden, setMfaOverridden] = useState(false);
  const [mfaGroupRequired, setMfaGroupRequired] = useState(false);

  const mfaEntityId = scope === 'group' ? groupId : schoolId;
  // A school inside a MAT can inherit the group's MFA policy.
  const inheritGroupId = scope === 'school' ? parentGroupId : null;

  const [addOpen, setAddOpen] = useState(false);
  const [activeAction, setActiveAction] = useState(null); // { key, user }

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const request = buildSearchRequest({
      scope,
      schoolId,
      groupId,
      page: page + 1, // API paging is 1-based; MUI is 0-based.
      pageSize,
      sortBy,
      sortDescending,
      searchTerm,
      status: statusFilter,
    });
    searchUsers(request)
      .then((result) => {
        if (!active) return;
        setUsers(result.users.map(mapUser));
        setTotal(result.total);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setUsers([]);
        setTotal(undefined);
        setError(err?.message || 'Could not load users. Please try again.');
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [scope, schoolId, groupId, page, pageSize, sortBy, sortDescending, searchTerm, statusFilter, reloadToken]);

  // Hydrate the "Require MFA" toggle from the server so it reflects the real org policy
  // instead of always showing OFF. For a school inside a MAT we also read the parent group's
  // policy: a group with MFA on enforces it across its schools, so the school toggle must
  // show ON (inherited, read-only). An unreadable response disables the toggle (see below).
  useEffect(() => {
    let active = true;
    setMfaLoading(true);
    setMfaInherited(false);
    setMfaOverridden(false);

    const ownRequest = getOrgMfaEnrollment({ entityType: SCOPE_TYPE[scope], entityId: mfaEntityId });
    const groupRequest =
      inheritGroupId !== undefined && inheritGroupId !== null
        ? getOrgMfaEnrollment({ entityType: SCOPE_TYPE.group, entityId: inheritGroupId }).catch(() => null)
        : Promise.resolve(null);

    Promise.all([ownRequest, groupRequest])
      .then(([ownResponse, groupResponse]) => {
        if (!active) return;
        const ownLevel = interpretOrgMfaLevel(ownResponse); // 'required' | 'optional' | 'none' | undefined
        const groupRequired = groupResponse ? interpretOrgMfaEnrolled(groupResponse) === true : false;
        setMfaReadable(ownLevel !== undefined);
        setMfaGroupRequired(groupRequired);
        // A school's own policy wins (precedence School > Group); with no own policy it inherits
        // the group. 'optional' is an explicit opt-out that overrides an enforcing group.
        const effective =
          ownLevel === 'required' ? true : ownLevel === 'optional' ? false : groupRequired;
        setMfaEnabled(effective);
        setMfaInherited(ownLevel === 'none' && groupRequired);
        setMfaOverridden(ownLevel === 'optional' && groupRequired);
        setMfaLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setMfaReadable(false);
        setMfaLoading(false);
      });
    return () => {
      active = false;
    };
  }, [scope, mfaEntityId, inheritGroupId]);

  const handleSort = (field) => {
    const fieldKey = SORT_FIELDS[field];
    if (sortBy === fieldKey) {
      setSortDescending((desc) => !desc);
    } else {
      setSortBy(fieldKey);
      setSortDescending(false);
    }
    setPage(0);
  };

  const handleAction = (key, user) => setActiveAction({ key, user });

  const closeAction = () => setActiveAction(null);

  const handleMfaToggle = async (event) => {
    const enabled = event.target.checked;
    const previous = mfaEnabled;
    const prevInherited = mfaInherited;
    const prevOverridden = mfaOverridden;
    setMfaEnabled(enabled);
    setMfaBusy(true);
    setMfaError('');
    try {
      await setOrgMfaEnrollment({
        entityType: SCOPE_TYPE[scope],
        entityId: mfaEntityId,
        enabled,
        // Only meaningful for a school inside a MAT: turning off while the group enforces MFA
        // writes an explicit opt-out override rather than deleting.
        parentGroupId: inheritGroupId,
      });
      // Reflect the new resolved state: on ⇒ the school's own Required policy; off while the
      // group enforces ⇒ an explicit opt-out override; off otherwise ⇒ no policy.
      if (enabled) {
        setMfaInherited(false);
        setMfaOverridden(false);
      } else {
        const wroteOverride = inheritGroupId !== undefined && inheritGroupId !== null && mfaGroupRequired;
        setMfaInherited(false);
        setMfaOverridden(wroteOverride);
      }
    } catch (err) {
      setMfaEnabled(previous);
      setMfaInherited(prevInherited);
      setMfaOverridden(prevOverridden);
      setMfaError(err?.message || 'The MFA setting could not be updated.');
    } finally {
      setMfaBusy(false);
    }
  };

  const changeEmailUser = activeAction?.key === 'changeEmail' ? activeAction.user : null;
  const confirmConfig = useMemo(() => {
    if (!activeAction || activeAction.key === 'changeEmail') return null;
    return CONFIRM_ACTIONS[activeAction.key];
  }, [activeAction]);

  return (
    <Card>
      <Section>
        <SectionHeaderRow title='Access Management' size='md'>
          <SearchBox>
            <SearchIcon />
            <input
              type='text'
              placeholder='Search user…'
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              aria-label='Search users'
            />
          </SearchBox>
          <StatusFilter>
            <span>Status</span>
            <StatusFilterMenu
              value={statusFilter}
              options={STATUS_FILTER_OPTIONS}
              onChange={(value) => {
                setStatusFilter(value);
                setPage(0);
              }}
            />
          </StatusFilter>
          <MfaControl>
            <Tooltip
              title={
                mfaInherited
                  ? 'Required by this school’s group. Turn off to opt this school out of the group’s MFA.'
                  : mfaOverridden
                  ? 'This school is opted out of its group’s MFA requirement. Turn on to require MFA again.'
                  : !mfaLoading && !mfaReadable
                  ? 'The current MFA policy could not be read, so it cannot be changed here.'
                  : ''
              }
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={mfaEnabled}
                    onChange={handleMfaToggle}
                    disabled={mfaBusy || mfaLoading || !mfaReadable}
                    sx={{ '& .Mui-checked': { color: sa.color.teal } }}
                  />
                }
                label={
                  mfaInherited
                    ? 'Require MFA (from group)'
                    : mfaOverridden
                    ? 'Require MFA (opted out)'
                    : 'Require MFA'
                }
              />
            </Tooltip>
            {mfaLoading && <CircularProgress size={16} sx={{ color: sa.color.teal }} />}
          </MfaControl>
          <PillButton type='button' $variant='danger' $size='lg' onClick={() => setAddOpen(true)}>
            Add User
          </PillButton>
        </SectionHeaderRow>

        {mfaError && (
          <Alert severity='error' onClose={() => setMfaError('')}>
            {mfaError}
          </Alert>
        )}

        <UsersTable
          scope={scope}
          users={users}
          total={total}
          page={page}
          pageSize={pageSize}
          sortBy={sortBy}
          sortDescending={sortDescending}
          loading={loading}
          error={error}
          onSortChange={handleSort}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          onAction={handleAction}
          schoolNamesById={schoolNamesById}
          statusFilter={statusFilter}
        />

        <AddUserModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          scope={scope}
          schoolId={schoolId}
          groupId={groupId}
          parentGroupId={parentGroupId}
          scopeName={scopeName}
          siteOptions={siteOptions}
          onCompleted={reload}
        />

        {changeEmailUser && (
          <ChangeEmailModal
            open
            onClose={closeAction}
            user={changeEmailUser}
            onCompleted={reload}
          />
        )}

        {confirmConfig && (
          <ConfirmActionModal
            open
            onClose={closeAction}
            title={confirmConfig.title}
            description={confirmConfig.describe(activeAction.user)}
            confirmLabel={confirmConfig.confirmLabel}
            successMessage={confirmConfig.successMessage}
            destructive={confirmConfig.destructive}
            action={() => confirmConfig.run(activeAction.user)}
            onCompleted={reload}
          />
        )}
      </Section>
    </Card>
  );
}

UserManagementSection.propTypes = {
  scope: PropTypes.oneOf(['school', 'group']).isRequired,
  schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scopeName: PropTypes.string,
  // { [schoolId]: schoolName } — supplied by the group account view to resolve the School Name column.
  schoolNamesById: PropTypes.object,
};

export default UserManagementSection;
